<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAToast } from '@Atoms';
import { applyShelterCurrency } from '@Helpers';
import type { CartAnimal, CustomAdditionalFeeDetail } from '@ShoppingCartModule';
import type { FieldSchema } from '../../../types';
import { AFieldInputText, AFieldMoney } from '../../atoms';
import {
  AddCustomAdditionalFeeService,
  RemoveCustomAdditionalFeeService,
  useShoppingCartStore,
} from '@ShoppingCartModule';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  disabled?: boolean;
  error?: string | boolean;
  field?: FieldSchema;
  placeholder?: string;
  required?: boolean;
}>();

const { animals, cartData } = storeToRefs(useShoppingCartStore());

const { notify, TOAST_TYPES } = useAToast();

const TOAST_ERROR = {
  message: 'cart.toast.outcomeInfoUpdate.error.message',
  title: 'cart.toast.outcomeInfoUpdate.error.title',
  type: TOAST_TYPES.ERROR,
};

const amountModel = ref<number | null>(null);
const descriptionModel = ref<string>('');
const loading = ref(false);
const _feesList = ref<CustomAdditionalFeeDetail[]>([]); // NOTE :: synced from cart and from add/remove responses

const animalId = computed(() => props.field?.settings?.animalId ?? '');
const animal = computed(() => animals.value.find(({ _id }: CartAnimal) => _id === animalId.value));
const feesFromCart = computed(() => animal.value?.additional_fees_details ?? []);

const canAddNewFee = computed(() => {
  const amount = amountModel.value ?? 0;
  const desc = (descriptionModel.value ?? '').trim();

  return amount >= 0.01 && desc.length > 0 && desc.length <= 255;
});

watch(feesFromCart, (newFees) => (_feesList.value = newFees), { immediate: true });

// HELPERS
function onAddNewFee() {
  const id = animalId.value;
  if (!id || !canAddNewFee.value || loading.value) return;

  const amount = amountModel.value ?? 0;
  const description = (descriptionModel.value ?? '').trim();

  loading.value = true;

  AddCustomAdditionalFeeService({ animal_id: id, amount, description })
    .then(({ data }) => {
      cartData.value = data;
      amountModel.value = null;
      descriptionModel.value = '';
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

function onRemove(feeIndex: number) {
  if (loading.value) return;

  loading.value = true;

  RemoveCustomAdditionalFeeService({ animal_id: animalId.value, fee_index: feeIndex })
    .then(({ data }) => (cartData.value = data))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}
</script>

<template>
  <div class="space-y-2">
    <div
      v-if="_feesList.length"
      class="space-y-1.5"
    >
      <div
        v-for="(fee, index) in _feesList"
        class="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
        :key="`${index}-${fee.description}-${fee.amount}`"
      >
        <span class="text-sm text-gray-700">{{ fee.description }}</span>

        <div class="flex items-center gap-2">
          <span class="text-sm font-medium">{{ applyShelterCurrency(parseFloat(String(fee.amount ?? 0))) }}</span>

          <Button
            text
            class="h-8 w-8 shrink-0 p-0"
            icon="pi pi-trash"
            severity="secondary"
            :loading
            :aria-label="$t('common.remove')"
            @click="onRemove(index)"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-wrap items-end gap-2">
      <div class="w-full min-w-[120px] flex-1">
        <AFieldInputText
          v-model="descriptionModel"
          :disabled
          :error="!!error"
          :placeholder="placeholder ? $t(placeholder) : $t('components.forms.placeholder.common')"
        />
      </div>

      <div class="max-w-[100px] flex-1">
        <AFieldMoney
          v-model="amountModel"
          customClass="max-w-full"
          customInputClass="max-w-full"
          :disabled
          :error="!!error"
          :placeholder="$t('cart.summary.amount')"
        />
      </div>

      <Button
        class="af-button af-button-success h-9 shrink-0"
        size="small"
        :disabled="!canAddNewFee || loading || disabled"
        :label="$t('common.add')"
        @click="onAddNewFee"
      />
    </div>
  </div>
</template>
